import type {
  GameState,
  PlayerId,
  SolarSystemMap,
} from '../../shared/types/domain';
import type { PlanningState } from '../game/planning';
import {
  buildAstrogationCoursePreviewViews,
  type CourseArrowView,
  type CourseCrashMarkerView,
  type CoursePreviewView,
} from './course';
import type { DrawShipIconInput } from './draw';
import { drawCourseMarkerView, drawWeakGravityMarkerView } from './markers';
import { scaledFont } from './text';
import { screenDash, screenLineWidth } from './visibility';

export type DrawShipIconFn = (input: DrawShipIconInput) => void;

const drawDriftSegment = (
  ctx: CanvasRenderingContext2D,
  seg: CoursePreviewView['driftSegments'][number],
  zoom: number,
): void => {
  ctx.save();
  ctx.globalAlpha = seg.alpha;
  ctx.strokeStyle = seg.color;
  ctx.lineWidth = screenLineWidth(1.65, zoom);
  ctx.shadowColor = seg.color;
  ctx.shadowBlur = 3;
  ctx.setLineDash(screenDash([4, 6], zoom));
  ctx.beginPath();
  ctx.moveTo(seg.points[0].x, seg.points[0].y);
  for (let i = 1; i < seg.points.length; i++) {
    ctx.lineTo(seg.points[i].x, seg.points[i].y);
  }
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
};

const drawCourseArrow = (
  ctx: CanvasRenderingContext2D,
  arrow: CourseArrowView,
  zoom: number,
): void => {
  ctx.save();
  ctx.strokeStyle = arrow.color;
  ctx.lineWidth = screenLineWidth(arrow.lineWidth, zoom);
  ctx.shadowColor = arrow.color;
  ctx.shadowBlur = 6;
  // Draw the head as one path so the tip uses canvas line joining
  // instead of two butt-end strokes colliding.
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(arrow.from.x, arrow.from.y);
  ctx.lineTo(arrow.to.x, arrow.to.y);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(arrow.headLeft.x, arrow.headLeft.y);
  ctx.lineTo(arrow.to.x, arrow.to.y);
  ctx.lineTo(arrow.headRight.x, arrow.headRight.y);
  ctx.stroke();
  ctx.restore();
};

const drawPendingGravityArrow = (
  ctx: CanvasRenderingContext2D,
  arrow: CourseArrowView,
  zoom: number,
): void => {
  ctx.save();
  ctx.strokeStyle = arrow.color;
  ctx.lineWidth = screenLineWidth(arrow.lineWidth, zoom);
  ctx.shadowColor = arrow.color;
  ctx.shadowBlur = 4;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.setLineDash(screenDash([3, 3], zoom));
  ctx.beginPath();
  ctx.moveTo(arrow.from.x, arrow.from.y);
  ctx.lineTo(arrow.to.x, arrow.to.y);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(arrow.headLeft.x, arrow.headLeft.y);
  ctx.lineTo(arrow.to.x, arrow.to.y);
  ctx.lineTo(arrow.headRight.x, arrow.headRight.y);
  ctx.stroke();
  ctx.restore();
};

const drawCourseCrashMarker = (
  ctx: CanvasRenderingContext2D,
  marker: CourseCrashMarkerView,
  zoom: number,
): void => {
  const { x, y } = marker.position;
  const radius = 11;
  ctx.save();
  ctx.fillStyle = 'rgba(255, 68, 68, 0.35)';
  ctx.strokeStyle = '#ff5555';
  ctx.lineWidth = screenLineWidth(2.5, zoom);
  ctx.shadowColor = 'rgba(255, 80, 80, 0.65)';
  ctx.shadowBlur = 8;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  const arm = 6;
  ctx.beginPath();
  ctx.moveTo(x - arm, y - arm);
  ctx.lineTo(x + arm, y + arm);
  ctx.moveTo(x + arm, y - arm);
  ctx.lineTo(x - arm, y + arm);
  ctx.stroke();
  ctx.restore();
};

const drawPreviewPolyline = (
  ctx: CanvasRenderingContext2D,
  preview: CoursePreviewView,
  zoom: number,
): void => {
  ctx.save();
  ctx.strokeStyle = preview.lineColor;
  ctx.lineWidth = screenLineWidth(preview.lineWidth, zoom);
  ctx.shadowColor = preview.lineColor;
  ctx.shadowBlur = preview.lineDash.length > 0 ? 4 : 7;
  ctx.setLineDash(screenDash(preview.lineDash, zoom));
  ctx.beginPath();
  ctx.moveTo(preview.linePoints[0].x, preview.linePoints[0].y);
  for (let i = 1; i < preview.linePoints.length; i++) {
    ctx.lineTo(preview.linePoints[i].x, preview.linePoints[i].y);
  }
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
};

const drawTakeoffSegment = (
  ctx: CanvasRenderingContext2D,
  segment: { points: { x: number; y: number }[] },
  zoom: number,
): void => {
  if (segment.points.length < 2) return;
  ctx.save();
  ctx.strokeStyle = 'rgba(100, 220, 255, 0.68)';
  ctx.lineWidth = screenLineWidth(1.6, zoom);
  ctx.shadowColor = 'rgba(100, 220, 255, 0.45)';
  ctx.shadowBlur = 4;
  ctx.setLineDash(screenDash([4, 4], zoom));
  ctx.beginPath();
  ctx.moveTo(segment.points[0].x, segment.points[0].y);
  for (let i = 1; i < segment.points.length; i++) {
    ctx.lineTo(segment.points[i].x, segment.points[i].y);
  }
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.restore();
};

const drawSingleCoursePreview = (
  ctx: CanvasRenderingContext2D,
  preview: CoursePreviewView,
  drawShipIcon: DrawShipIconFn,
  playerId: PlayerId,
  zoom: number,
): void => {
  if (preview.takeoffSegment) {
    drawTakeoffSegment(ctx, preview.takeoffSegment, zoom);
  }
  drawPreviewPolyline(ctx, preview, zoom);
  for (const arrow of preview.gravityArrows) {
    drawCourseArrow(ctx, arrow, zoom);
  }
  for (const arrow of preview.pendingGravityArrows) {
    drawPendingGravityArrow(ctx, arrow, zoom);
  }
  for (const seg of preview.driftSegments) {
    drawDriftSegment(ctx, seg, zoom);
  }
  if (preview.crashMarker) {
    drawCourseCrashMarker(ctx, preview.crashMarker, zoom);
  }
  if (preview.ghostShip) {
    const g = preview.ghostShip;
    drawShipIcon({
      ctx,
      x: g.position.x,
      y: g.position.y,
      owner: g.owner,
      playerId,
      alpha: g.alpha,
      heading: g.heading,
      disabledTurns: 0,
      shipType: g.shipType,
      zoom,
    });
  }
  for (const marker of [...preview.burnMarkers, ...preview.overloadMarkers]) {
    drawCourseMarkerView(ctx, marker, zoom);
  }
  if (preview.burnArrow) {
    drawCourseArrow(ctx, preview.burnArrow, zoom);
  }
  if (preview.overloadArrow) {
    drawCourseArrow(ctx, preview.overloadArrow, zoom);
  }
  for (const marker of preview.weakGravityMarkers) {
    drawWeakGravityMarkerView(ctx, marker, zoom);
  }
  if (preview.fuelCostLabel) {
    const f = preview.fuelCostLabel;
    ctx.fillStyle = f.color;
    ctx.font = scaledFont('bold 11px monospace', zoom);
    ctx.textAlign = 'center';
    ctx.fillText(f.text, f.position.x, f.position.y);
  }
};

export type DrawAstrogationCoursePreviewLayerInput = {
  ctx: CanvasRenderingContext2D;
  state: GameState;
  playerId: PlayerId;
  planningState: PlanningState;
  map: SolarSystemMap;
  hexSize: number;
  drawShipIcon: DrawShipIconFn;
  zoom: number;
  showTrainingRecommendation?: boolean;
};

export const drawAstrogationCoursePreviewLayer = (
  input: DrawAstrogationCoursePreviewLayerInput,
): void => {
  const {
    ctx,
    state,
    playerId,
    planningState,
    map,
    hexSize,
    drawShipIcon,
    zoom,
    showTrainingRecommendation = false,
  } = input;
  for (const preview of buildAstrogationCoursePreviewViews(
    state,
    playerId,
    planningState,
    map,
    hexSize,
    showTrainingRecommendation,
  )) {
    drawSingleCoursePreview(ctx, preview, drawShipIcon, playerId, zoom);
  }
};
